import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Send, Clock, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { SEOPage } from '@/components/SEO';
import { submitContact } from '@/lib/api';

const HERO_BG = "https://customer-assets.emergentagent.com/job_ae021ef0-7027-4ae5-b97f-cd7f1827f457/artifacts/z1lv2e2v_P2341000.JPG";

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await submitContact(formData);
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: Mail,
      title: 'Email Us',
      details: 'brian@paisley.org.uk',
      subtext: 'We respond within 24 hours'
    },
    {
      icon: Phone,
      title: 'Call Us',
      details: '07957 166339',
      subtext: 'Mon-Fri, 9am-5pm'
    },
    {
      icon: MapPin,
      title: 'Visit Us',
      details: 'Paisley, Renfrewshire',
      subtext: 'Scotland, UK'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="contact-page">
      <SEOPage.Contact />
      <Navbar />
      
      {/* Hero Section */}
      <section 
        className="relative pt-32 pb-20"
        style={{
          backgroundImage: `url(${HERO_BG})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/90 to-slate-900/95" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="mb-4 bg-sky-500/20 text-sky-400 border-sky-500/30">
            Get in Touch
          </Badge>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight">
            Contact Us
          </h1>
          <p className="text-slate-300 text-lg max-w-2xl mx-auto">
            Have questions about sponsorship or want to discuss advertising opportunities? 
            We'd love to hear from you.
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 -mt-10" data-testid="contact-info-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-6">
            {contactInfo.map((info, index) => (
              <Card 
                key={index} 
                className="bg-white border-0 shadow-xl hover:shadow-2xl transition-shadow"
                data-testid={`contact-info-${index}`}
              >
                <CardContent className="p-6 text-center">
                  <div className="inline-flex items-center justify-center w-14 h-14 bg-sky-100 rounded-2xl mb-4">
                    <info.icon className="h-7 w-7 text-sky-600" />
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-slate-900 mb-1">
                    {info.title}
                  </h3>
                  <p className="text-slate-700 font-medium">{info.details}</p>
                  <p className="text-slate-500 text-sm mt-1">{info.subtext}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20" data-testid="contact-form-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Form */}
            <div>
              <h2 className="font-heading text-3xl font-bold text-slate-900 mb-2">
                Send Us a Message
              </h2>
              <p className="text-slate-600 mb-8">
                Fill out the form below and we'll get back to you as soon as possible.
              </p>
              
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="John Smith"
                      className="bg-white border-slate-200 h-12"
                      required
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="john@example.com"
                      className="bg-white border-slate-200 h-12"
                      required
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>
                
                <div className="grid sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+44 7XXX XXXXXX"
                      className="bg-white border-slate-200 h-12"
                      data-testid="contact-phone-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="subject">Subject *</Label>
                    <Input
                      id="subject"
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      placeholder="Sponsorship Inquiry"
                      className="bg-white border-slate-200 h-12"
                      required
                      data-testid="contact-subject-input"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="message">Your Message *</Label>
                  <Textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="Tell us how we can help..."
                    className="bg-white border-slate-200 min-h-[150px] resize-none"
                    required
                    data-testid="contact-message-input"
                  />
                </div>
                
                <Button 
                  type="submit" 
                  disabled={loading}
                  className="w-full sm:w-auto bg-sky-500 hover:bg-sky-600 text-white px-8 py-6 text-base"
                  data-testid="contact-submit-btn"
                >
                  {loading ? (
                    <>Sending...</>
                  ) : (
                    <>
                      Send Message
                      <Send className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </form>
            </div>

            {/* Info Panel */}
            <div className="lg:pl-8">
              <div className="bg-slate-900 rounded-2xl p-8 text-white mb-8">
                <div className="flex items-center gap-3 mb-6">
                  <MessageSquare className="h-6 w-6 text-sky-400" />
                  <h3 className="font-heading text-xl font-semibold">Quick Response</h3>
                </div>
                <p className="text-slate-300 mb-6">
                  We understand time is valuable. Our team aims to respond to all inquiries 
                  within 24 hours during business days.
                </p>
                <div className="flex items-center gap-2 text-sky-400">
                  <Clock className="h-4 w-4" />
                  <span className="text-sm">Average response time: 4 hours</span>
                </div>
              </div>
              
              <div className="bg-sky-50 rounded-2xl p-8 border border-sky-100">
                <h3 className="font-heading text-xl font-semibold text-slate-900 mb-4">
                  Looking to Sponsor?
                </h3>
                <p className="text-slate-600 mb-6">
                  Ready to get started? Check out our sponsorship packages and register 
                  your business directly.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Link to="/sponsors" className="flex-1">
                    <Button variant="outline" className="w-full border-sky-200 text-sky-700 hover:bg-sky-100">
                      View Packages
                    </Button>
                  </Link>
                  <a href="https://paisley.org.uk/sponsors" className="flex-1">
                    <Button className="w-full bg-sky-500 hover:bg-sky-600 text-white">
                      Register Now
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
